// PlateOverlayControls.tsx — Floating controls for the approach plate overlay.
//
// Renders ONLY when a plate overlay is active. Plates are selected from the
// airport detail → Procedures tab → 🗺 flow. When overlay is active:
//   - Plate label + "Dismiss" button
//   - Opacity slider (0–100%)
//   - "Fix this plate" CTA when georef is LC/fallback
//
// Positioned in the bottom-left corner of the map, above the instrument strip.
//
// (Previously included a 'Demo plate overlay' launch button. Boss removed it
// 2026-05-16 — it was engineering scaffolding crowding the map controls.
// Real plates reach the overlay via the procedures tab; the demo path isn't
// needed for any user-facing flow.)

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '../stores/uiStore';

// (2026-05-18 boss report) The old approach measured the cluster's
// height and added it to the instrument-strip height, then offset the
// overlay card by the sum. That works on mobile where the cluster
// sits flush above the strip, but on DESKTOP the cluster falls back
// to `bottom: 11rem` (176px) when no instrument strip is shown, and
// our height+strip math drifts — the card ends up overlapping the
// cluster, with the Record button visibly underneath when the user
// hovers it.
//
// Right approach: measure the cluster's actual top edge in viewport
// coordinates via getBoundingClientRect(), then compute the overlay's
// `bottom` as (viewport_height - cluster_top + gap). That works
// regardless of whether the instrument strip is visible.
const AERO_CLUSTER_GAP_PX = 12;
const FALLBACK_BOTTOM_PX = 240; // matches old behavior when measurement fails

function useBottomOffsetAboveCluster(): number {
  const [px, setPx] = useState<number>(FALLBACK_BOTTOM_PX);
  useEffect(() => {
    function measure() {
      const el = document.querySelector<HTMLElement>('[data-fy-aero-cluster]');
      if (!el) { setPx(FALLBACK_BOTTOM_PX); return; }
      const rect = el.getBoundingClientRect();
      if (rect.height === 0) { setPx(FALLBACK_BOTTOM_PX); return; }
      // Viewport-relative top of the cluster + the gap we want above it.
      const fromBottom = window.innerHeight - rect.top + AERO_CLUSTER_GAP_PX;
      setPx(fromBottom > 0 ? fromBottom : FALLBACK_BOTTOM_PX);
    }
    measure();
    const el = document.querySelector<HTMLElement>('[data-fy-aero-cluster]');
    const ro = el ? new ResizeObserver(measure) : null;
    if (el && ro) ro.observe(el);
    window.addEventListener('resize', measure);
    const t1 = window.setTimeout(measure, 300);
    const t2 = window.setTimeout(measure, 1000);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);
  return px;
}

export function PlateOverlayControls() {
  const activePlateOverlay = useUiStore((s) => s.activePlateOverlay);
  const plateOverlayOpacity = useUiStore((s) => s.plateOverlayOpacity);
  const setPlateOverlay = useUiStore((s) => s.setPlateOverlay);
  const setPlateOverlayOpacity = useUiStore((s) => s.setPlateOverlayOpacity);
  const plateOverlayShowFullPage = useUiStore((s) => s.plateOverlayShowFullPage);
  const setPlateOverlayShowFullPage = useUiStore((s) => s.setPlateOverlayShowFullPage);
  const plateOverlayShowOwnship = useUiStore((s) => s.plateOverlayShowOwnship);
  const setPlateOverlayShowOwnship = useUiStore((s) => s.setPlateOverlayShowOwnship);
  const plateOverlayResult = useUiStore((s) => s.plateOverlayResult);

  // Bottom-left of the map is shared with the AeroKnobCluster (record /
  // Aero / Deep stack). Sit ABOVE the cluster so neither covers the
  // other. The cluster's actual viewport-top edge is live-measured
  // and we add a gap above it. Works on both mobile (instrument strip
  // visible) and desktop (cluster at fixed bottom: 11rem fallback).
  const bottomOffsetPx = useBottomOffsetAboveCluster();

  // (2026-05-18) The 'Fly to' button was removed — it called recenter()
  // on the plate center but did nothing visible because the map was
  // already showing the overlay (which is bounded by the plate corners).
  // The Dismiss button was replaced with a standard close-X in the
  // upper-right of the card; boss preferred the conventional
  // dismiss-affordance over a labeled button.

  const handleDismiss = () => {
    setPlateOverlay(null);
  };

  const navigate = useNavigate();
  const handleFixPlate = () => {
    // Route to admin queue with query params so the page auto-opens
    // the editor for the current plate.
    if (!activePlateOverlay) return;
    const params = new URLSearchParams({
      airport: activePlateOverlay.airportIdent,
      pdf: activePlateOverlay.pdfName,
    });
    navigate(`/admin/plate-corrections?${params.toString()}`);
  };

  // Always show the "Fix this plate" CTA. Originally this only surfaced
  // when the georef was LC / fallback / spec-corners, but boss request
  // 2026-05-25 13:03 PT: pilots flying real-world routes routinely catch
  // misalignments on plates the auto-georef rated 'ok' (or that a prior
  // cartographer 'fixed' badly). They should be able to submit a fresh
  // correction without having to navigate out to /admin/plate-corrections
  // by hand. The button shows for every active overlay.
  const showFixButton = Boolean(plateOverlayResult);
  // Tweak the wording when the plate is already good, so users don't
  // think they're being told it's broken.
  const fixButtonLabel =
    plateOverlayResult && plateOverlayResult.status === 'ok'
      ? '🛠 Submit correction'
      : '🛠 Fix this plate';
  const fixButtonTitle =
    plateOverlayResult && plateOverlayResult.status === 'ok'
      ? 'Looks misaligned? Submit a correction.'
      : 'Open the plate-correction editor for this plate';

  return (
    <div
      className="absolute z-20 left-3 flex flex-col gap-1.5 pointer-events-auto"
      style={{ bottom: `${bottomOffsetPx}px`, maxWidth: '220px' }}
    >
      {activePlateOverlay && (
        /* Active overlay controls. Component renders nothing when no overlay
         * is selected — the demo button was removed 2026-05-16. */
        <div className="rounded-lg bg-cockpit-800/90 border border-cockpit-600 backdrop-blur shadow-bar text-cockpit-100 flex flex-col gap-1">
          {/* Header */}
          <div className="px-2.5 pt-2 flex items-start gap-1.5">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-cockpit-400 uppercase tracking-wide">Plate overlay</div>
              <div className="text-[11px] font-semibold truncate text-cockpit-100">
                {activePlateOverlay.airportIdent} · {activePlateOverlay.chartName}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Remove plate overlay"
              title="Remove overlay from map"
              className="shrink-0 -mt-0.5 -mr-0.5 h-5 w-5 rounded text-cockpit-400 hover:text-cockpit-50 hover:bg-cockpit-700 flex items-center justify-center text-[12px] leading-none"
            >
              ✕
            </button>
          </div>

          {/* Cockpit-awareness banner for low-confidence / fallback corners.
              Renders an amber strip when the server-side georef was
              low-confidence (residual 0.1–5 NM) or when we fell back to
              CIFP-bbox / pre-baked spec corners (typically off by miles). */}
          {plateOverlayResult && plateOverlayResult.status !== 'ok' && (
            <div
              className="mx-2 my-0.5 px-2 py-1 rounded border text-[10px] leading-tight bg-ember-500/10 border-ember-500/60 text-ember-200 flex items-start gap-1.5"
              role="alert"
              aria-label="Plate georeference confidence warning"
            >
              <span className="text-ember-400 shrink-0 leading-none" aria-hidden>⚠️</span>
              <span className="min-w-0">
                {plateOverlayResult.status === 'low-confidence' && (
                  <>
                    <strong>Low confidence</strong>
                    {typeof plateOverlayResult.residualNm === 'number' && (
                      <> · residual {plateOverlayResult.residualNm.toFixed(2)} NM</>
                    )}
                    . Verify visually before relying on placement.
                  </>
                )}
                {plateOverlayResult.status === 'fallback-cifp' && (
                  <>
                    <strong>Bounding-box fallback</strong>. No precise georef
                    available—plate position is approximate. Visual reference
                    only.
                  </>
                )}
                {plateOverlayResult.status === 'spec-corners' && (
                  <>
                    <strong>Demo corners</strong>. Pre-baked placement; not a
                    live georef.
                  </>
                )}
              </span>
            </div>
          )}

          {/* Opacity slider */}
          <div className="px-2.5 flex items-center gap-2">
            <span className="text-[10px] text-cockpit-400 w-8 shrink-0">
              {Math.round(plateOverlayOpacity * 100)}%
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={plateOverlayOpacity}
              onChange={(e) => setPlateOverlayOpacity(parseFloat(e.target.value))}
              className="flex-1 accent-ember-500"
              aria-label="Plate overlay opacity"
            />
          </div>

          {/* Full-page toggle.
              Pill dimensions match the canonical AeroSettingsSheet
              ToggleRow (w-9 h-5 with w-4 h-4 thumb, 2px inset top+left)
              so the white thumb stays inside the pill in BOTH states.
              The previous w-8 h-4 / w-3 h-3 sizing was off by 2px in
              ON state and clipped to the right; the boss reported it
              looking funny on 2026-05-15. */}
          {/* Show-full-chart toggle. Only meaningful when the overlay is
              backed by real server-georef corners (status ok/low-confidence).
              Spec-corners + CIFP-bbox fallback paths don't provide pageCorners,
              so flipping the toggle would silently do nothing. Disable the
              control + explain in a tooltip when that's the case (boss
              reported the silent-no-op on 2026-05-16 — boss tapped, nothing
              happened, no feedback). */}
          {(() => {
            const fullChartAvailable =
              plateOverlayResult?.status === 'ok' ||
              plateOverlayResult?.status === 'low-confidence';
            return (
              <div className="px-2.5 flex items-center justify-between gap-2">
                <span
                  className={
                    'text-[10px] ' +
                    (fullChartAvailable ? 'text-cockpit-400' : 'text-cockpit-600')
                  }
                >
                  Show full chart
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={plateOverlayShowFullPage && fullChartAvailable}
                  aria-disabled={!fullChartAvailable}
                  onClick={() => {
                    if (!fullChartAvailable) return;
                    setPlateOverlayShowFullPage(!plateOverlayShowFullPage);
                  }}
                  className={
                    'relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ' +
                    (!fullChartAvailable
                      ? 'bg-cockpit-700 cursor-not-allowed opacity-50'
                      : plateOverlayShowFullPage
                      ? 'bg-ember-500'
                      : 'bg-cockpit-600')
                  }
                  title={
                    fullChartAvailable
                      ? 'Toggle full chart (header/minima/profile) vs plan view only'
                      : 'Full chart unavailable — this plate has no precise server georef (overlay is using a rough CIFP bbox)'
                  }
                >
                  <span
                    className={
                      'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-cockpit-100 transition-transform ' +
                      (plateOverlayShowFullPage && fullChartAvailable
                        ? 'translate-x-4'
                        : 'translate-x-0')
                    }
                  />
                </button>
              </div>
            );
          })()}

          {/* Ownship + route on plate toggle.
              Only functional when georef status === 'ok'; the layer itself
              gates rendering on status, but we still show the toggle so the
              pilot knows the feature exists and can pre-arm it. */}
          <div className="px-2.5 flex items-center justify-between gap-2">
            <span className="text-[10px] text-cockpit-400">Ownship + route on plate</span>
            <button
              type="button"
              role="switch"
              aria-checked={plateOverlayShowOwnship}
              onClick={() => setPlateOverlayShowOwnship(!plateOverlayShowOwnship)}
              className={
                'relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ' +
                (plateOverlayShowOwnship ? 'bg-ember-500' : 'bg-cockpit-600')
              }
              title="Show ownship aircraft and active route overlaid on the plate"
            >
              <span
                className={
                  'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-cockpit-100 transition-transform ' +
                  (plateOverlayShowOwnship ? 'translate-x-4' : 'translate-x-0')
                }
              />
            </button>
          </div>

          {/* Fix-this-plate CTA — always shown when an overlay is active.
              Wording shifts to 'Submit correction' for plates the auto-
              georef rated 'ok' so the button feels invitational, not a
              warning. */}
          {showFixButton && (
            <div className="px-2.5">
              <button
                onClick={handleFixPlate}
                className={
                  'w-full text-[11px] py-1 rounded text-white ' +
                  (plateOverlayResult?.status === 'ok'
                    ? 'bg-cockpit-700/80 hover:bg-cockpit-600 border border-cockpit-600'
                    : 'bg-ember-600/80 hover:bg-ember-500')
                }
                title={fixButtonTitle}
              >
                {fixButtonLabel}
              </button>
            </div>
          )}

          {/* Bottom padding so the card has consistent breathing room
              now that the Fly-to / Dismiss action row is gone. */}
          <div className="pb-2" />
        </div>
      )}
    </div>
  );
}
